import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { PINNED_PRESET_ROUTE_PATH } from '@/entities/settings';
import { type AppNavigateOptions, appGeneratePath, useAppNavigate } from '@/shared/lib';

/**
 * Адреса раздела, в котором открыт список диалогов: обычного («Диалоги») либо закреплённого пресета.
 *
 * Раздел определяется адресом, а не настройками: у раздела пресета свой сегмент, поэтому все
 * переходы к записи и обратно к списку строятся отсюда — иначе работа с записью выводила бы
 * из раздела пресета в общий список диалогов.
 */
export const useDialogsSectionRoute = () => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useAppNavigate();

  /** Адрес списка текущего раздела — куда ведёт возврат из записи */
  const listPath = useMemo(() => {
    if (slug) return appGeneratePath(PINNED_PRESET_ROUTE_PATH, { slug, id: null });
    return appGeneratePath('/dialogs/:id?', { id: null });
  }, [slug]);

  /** Адрес записи в текущем разделе — для ссылок и открытия в новом окне */
  const buildRecordPath = useCallback(
    (recordId: string) => {
      if (slug) return appGeneratePath(PINNED_PRESET_ROUTE_PATH, { slug, id: recordId });
      return appGeneratePath('/dialogs/:id', { id: recordId });
    },
    [slug],
  );

  /** Переход к записи текущего раздела; `null` — к списку раздела */
  const navigateToRecord = useCallback(
    (recordId: string | null, options?: AppNavigateOptions) => {
      if (slug) {
        navigate(PINNED_PRESET_ROUTE_PATH, { slug, id: recordId }, options);
        return;
      }
      navigate('/dialogs/:id?', { id: recordId }, options);
    },
    [slug, navigate],
  );

  return { listPath, buildRecordPath, navigateToRecord };
};
